import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import api from "../../store/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { DahliasLogo } from "../../components/brand/Logos";
import { setRemembered } from "../../admin/adminSession";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const nextPath = search.get("next") === "/console" ? "/console" : "/admin";
  const isManagement = nextPath === "/console";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password, remember }, { withCredentials: true });
      localStorage.setItem("heritage-admin-token", res.data.token);
      localStorage.setItem("heritage-admin-name", res.data.name || "Admin");
      localStorage.setItem("heritage-admin-email", (res.data.email || email || "").toLowerCase());
      setRemembered(remember);
      toast.success("Welcome back.");
      navigate(nextPath);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-[100svh] bg-[var(--dahlias-cream)] flex flex-col"
    >
      {/* Sage utility ticker */}
      <div className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)]">
        <div className="max-w-7xl mx-auto px-5 py-2 flex items-center justify-between text-[11px] font-label tracking-[0.35em]">
          <Link to="/" className="hover:opacity-80 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> BACK TO SITE
          </Link>
          <span className="hidden sm:inline">HERITAGE · ADMIN · EST. 1932</span>
          <span className="hidden sm:inline">PRIVATE</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-12">
        <form
          onSubmit={submit}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/30 p-8 md:p-10 wobble-a"
        >
          <div className="text-center">
            <DahliasLogo variant="sage" className="w-28 h-28 md:w-32 md:h-32 mx-auto" />
            <span className="block mt-5 font-label tracking-[0.4em] text-[10px] text-[var(--dahlias-sage-deep)]">
              ✦ {isManagement ? "HERITAGE MANAGEMENT" : "HERITAGE ADMIN"} ✦
            </span>
            <h1 className="font-display text-3xl md:text-4xl text-[var(--dahlias-forest)] mt-2 leading-tight">
              Sign <span className="font-display-italic text-[var(--dahlias-sage-deep)]">in.</span>
            </h1>
            <p className="text-[var(--dahlias-ink)]/65 mt-2">
              {isManagement ? "Pack, ship & deliver Heritage orders." : "Manage the shop, orders & site content."}
            </p>
          </div>

          <div className="mt-8 space-y-4">
            <div>
              <Label
                htmlFor="login-email"
                className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]"
              >
                Email
              </Label>
              <Input
                id="login-email"
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-14 text-base bg-[var(--dahlias-cream)] border-[var(--dahlias-sage)]/30 wobble-pill px-5"
              />
            </div>
            <div>
              <Label
                htmlFor="login-password"
                className="font-label tracking-[0.3em] text-xs uppercase text-[var(--dahlias-sage-deep)]"
              >
                Password
              </Label>
              <Input
                id="login-password"
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-14 text-base bg-[var(--dahlias-cream)] border-[var(--dahlias-sage)]/30 wobble-pill px-5"
              />
            </div>
            <label className="flex items-center gap-3 text-sm text-[var(--dahlias-ink)]/80 cursor-pointer select-none" data-testid="admin-login-remember-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="admin-login-remember"
                className="w-5 h-5 accent-[var(--dahlias-sage-deep)]" />
              <span>Remember this login <span className="text-[var(--dahlias-ink)]/50">· stays signed in on this device for 30 days</span></span>
            </label>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full h-14 text-base bg-[var(--dahlias-sage)] hover:bg-[var(--dahlias-sage-deep)] text-[var(--dahlias-cream)] wobble-pill font-medium tracking-wide shadow-md"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>
      </div>

      <footer className="bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)] text-center py-4 text-xs font-label tracking-[0.35em]">
        © {new Date().getFullYear()} HERITAGE DAHLIAS · FOR NANA JEAN
      </footer>
    </div>
  );
}
